import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { getDailyRangeOptions } from 'src/helpers/date_helper';
import { convertToUnit } from 'src/helpers/units_helpers';
import { formatSeriesLabel } from './chart_helper';
import type { ChartConfigSeriesData, ChartDataInterface } from 'src/types/Chart';
import type { StatisticsData } from '@Shelly/shelly-design-system/LayerChart';

export type LayerChartSummarySection = {
	summaryTitle: string;
	summary: string;
	aboutTitle: string;
	about: string;
};

type SeriesStats = {
	first: number;
	last: number;
	min: number;
	max: number;
	average: number;
};

type ChartSeries = NonNullable<ChartDataInterface['series']>[number];

const $format = get(format);
const capFirst = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const titleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());
const isMinSeries = (name = '') => /(^|_)min($|_)/.test(name);
const isMaxSeries = (name = '') => /(^|_)max($|_)/.test(name);
const isAverageSeries = (name = '') => /(^|_)(avg|average)($|_)/.test(name);

const readableName = (chartData: ChartDataInterface, name: string) => {
	if (chartData?.chartTitle) return chartData.chartTitle;
	const value = name || chartData?.name || '';
	const keyParts = value.split('_');
	if (!isNaN(Number(keyParts[keyParts.length - 1]))) keyParts.pop();
	return $format(`collapse_title_chart_${keyParts.join('_')}`, {
		default: formatSeriesLabel(value),
	}).toLowerCase();
};

const periodName = (chartData: ChartDataInterface) => {
	switch (chartData?.rangePeriod) {
		case 'hour':
			return $format('layer_chart_period_hour', { default: 'hour' });
		case '24_hours':
			return $format('layer_chart_period_24_hours', { default: '24 hours' });
		case 'day':
			return $format('layer_chart_period_day', { default: 'day' });
		case 'week':
			return $format('layer_chart_period_week', { default: 'week' });
		case '7_days':
			return $format('layer_chart_period_7_days', { default: '7 days' });
		case 'month':
			return $format('layer_chart_period_month', { default: 'month' });
		case '6_months':
			return $format('layer_chart_period_6_months', { default: '6 months' });
		case '12_months':
			return $format('layer_chart_period_12_months', { default: '12 months' });
		case 'year':
			return $format('layer_chart_period_year', { default: 'year' });
		case 'billing_date':
			return $format('layer_chart_period_billing', { default: 'billing period' });
		case 'custom':
			return $format('layer_chart_period_custom', { default: 'custom period' });
		default:
			return $format('layer_chart_period_default', { default: 'period' });
	}
};

const summaryTitle = (chartData: ChartDataInterface) => {
	const period = periodName(chartData);
	if (/^(24_hours|day)$/.test(chartData?.rangePeriod)) {
		return $format('layer_chart_summary_title_daily', { default: 'Daily Summary' });
	}
	if (chartData?.rangePeriod === 'hour') {
		return $format('layer_chart_summary_title_hourly', { default: 'Hourly Summary' });
	}
	return $format('layer_chart_summary_title', {
		default: `${capFirst(period)} Summary`,
		values: { period: capFirst(period) },
	});
};

const weekDayName = (timestamp: string | number) => {
	const day = new Date(timestamp).getDay();
	return getDailyRangeOptions().find((option) => option.value === (Number.isFinite(day) ? day : 1))
		?.title;
};

const periodIntro = (chartData: ChartDataInterface, data: ChartConfigSeriesData[]) => {
	const firstDate = data.find((point) => point.dt || point.date || point.datetime);
	const timestamp = firstDate?.dt || firstDate?.date || firstDate?.datetime;
	switch (chartData?.rangePeriod) {
		case '24_hours':
			return $format('layer_chart_period_intro_24_hours', { default: 'For the last 24 hours' });
		case 'day':
			return timestamp
				? $format('layer_chart_period_intro_on_date', {
						default: `On ${weekDayName(timestamp)}`,
						values: { date: weekDayName(timestamp) },
					})
				: $format('layer_chart_period_intro_day', { default: 'For this day' });
		case 'hour':
			return $format('layer_chart_period_intro_hour', { default: 'During this hour' });
		case 'week':
			return $format('layer_chart_period_intro_week', { default: 'During this week' });
		case '7_days':
			return $format('layer_chart_period_intro_7_days', { default: 'During these 7 days' });
		case 'month':
			return $format('layer_chart_period_intro_month', { default: 'During this month' });
		case '6_months':
			return $format('layer_chart_period_intro_6_months', { default: 'During these 6 months' });
		case '12_months':
			return $format('layer_chart_period_intro_12_months', {
				default: 'During these 12 months',
			});
		case 'year':
			return $format('layer_chart_period_intro_year', { default: 'During this year' });
		case 'billing_date':
			return $format('layer_chart_period_intro_billing', {
				default: 'During this billing period',
			});
		case 'custom':
			return $format('layer_chart_period_intro_custom', {
				default: 'During this custom period',
			});
		default:
			return $format('layer_chart_period_intro_default', { default: 'During this period' });
	}
};

const getValues = (data: ChartConfigSeriesData[] = []) =>
	data.map((point) => Number(point.value)).filter((value) => Number.isFinite(value));

const getStats = (data: ChartConfigSeriesData[]): SeriesStats | null => {
	const values = getValues(data);
	if (!values.length) return null;
	const total = values.reduce((sum, value) => sum + value, 0);
	return {
		first: values[0],
		last: values[values.length - 1],
		min: Math.min(...values),
		max: Math.max(...values),
		average: total / values.length,
	};
};

const getMinMaxStats = (series: ChartSeries[]): SeriesStats | null => {
	const minSeries = series.filter((item) => isMinSeries(item.name));
	const maxSeries = series.filter((item) => isMaxSeries(item.name));
	if (!minSeries.length || !maxSeries.length) return null;

	const minValues = minSeries.flatMap((item) => getValues(item.data));
	const maxValues = maxSeries.flatMap((item) => getValues(item.data));
	if (!minValues.length || !maxValues.length) return null;

	const averageSeries = series.filter((item) => isAverageSeries(item.name));
	const averageValues = averageSeries.flatMap((item) => getValues(item.data));
	const pointCount = Math.max(
		...minSeries.map((item) => item.data?.length || 0),
		...maxSeries.map((item) => item.data?.length || 0),
		...averageSeries.map((item) => item.data?.length || 0),
	);
	const trendValues = Array.from({ length: pointCount }, (_, index) => {
		const values = averageSeries.length
			? averageSeries.map((item) => Number(item.data?.[index]?.value))
			: [...minSeries, ...maxSeries].map((item) => Number(item.data?.[index]?.value));
		const validValues = values.filter((value) => Number.isFinite(value));
		return validValues.length
			? validValues.reduce((sum, value) => sum + value, 0) / validValues.length
			: NaN;
	}).filter((value) => Number.isFinite(value));
	if (!trendValues.length) return null;
	const averageSource = averageValues.length ? averageValues : [...minValues, ...maxValues];
	const total = averageSource.reduce((sum, value) => sum + value, 0);

	return {
		first: trendValues[0],
		last: trendValues[trendValues.length - 1],
		min: Math.min(...minValues),
		max: Math.max(...maxValues),
		average: total / averageSource.length,
	};
};

const valueLabel = (chartData: ChartDataInterface, value: number) =>
	convertToUnit(value, chartData?.units || '');

const trendText = (chartData: ChartDataInterface, stats: SeriesStats) => {
	const range = Math.abs(stats.max - stats.min);
	const baseline = Math.max(Math.abs(stats.average), 1);
	const delta = stats.last - stats.first;
	if (range / baseline <= 0.03) {
		return $format('layer_chart_trend_stable', {
			default: `stayed stable around ${valueLabel(chartData, stats.average)}`,
			values: { average: valueLabel(chartData, stats.average) },
		});
	}
	if (delta > range * 0.35) {
		return $format('layer_chart_trend_rose', {
			default: `rose from ${valueLabel(chartData, stats.first)} to ${valueLabel(chartData, stats.last)}`,
			values: {
				first: valueLabel(chartData, stats.first),
				last: valueLabel(chartData, stats.last),
			},
		});
	}
	if (delta < -range * 0.35) {
		return $format('layer_chart_trend_fell', {
			default: `fell from ${valueLabel(chartData, stats.first)} to ${valueLabel(chartData, stats.last)}`,
			values: {
				first: valueLabel(chartData, stats.first),
				last: valueLabel(chartData, stats.last),
			},
		});
	}
	return $format('layer_chart_trend_varied', {
		default: `varied around ${valueLabel(chartData, stats.average)}`,
		values: { average: valueLabel(chartData, stats.average) },
	});
};

const buildMeasurementSection = (
	chartData: ChartDataInterface,
	seriesName: string,
	data: ChartConfigSeriesData[],
	statsOverride: SeriesStats | null = null,
): LayerChartSummarySection | null => {
	const stats = statsOverride || getStats(data);
	if (!stats) return null;
	const name = readableName(chartData, seriesName);
	const intro = periodIntro(chartData, data);
	const trend = trendText(chartData, stats);
	return {
		summaryTitle: summaryTitle(chartData),
		summary: $format('layer_chart_measurement_summary', {
			default: `${intro}, the ${name} ${trend}.`,
			values: {
				intro,
				name,
				trend,
				min: valueLabel(chartData, stats.min),
				max: valueLabel(chartData, stats.max),
			},
		}),
		aboutTitle: $format('layer_chart_about_title', {
			default: `About ${titleCase(name)}`,
			values: { name: titleCase(name) },
		}),
		about: $format('layer_chart_measurement_about', {
			default: `Average: ${valueLabel(chartData, stats.average)}. Range: ${valueLabel(chartData, stats.min)} to ${valueLabel(chartData, stats.max)}.`,
			values: {
				intro,
				name,
				average: valueLabel(chartData, stats.average),
				min: valueLabel(chartData, stats.min),
				max: valueLabel(chartData, stats.max),
			},
		}),
	};
};

const buildConsumptionSection = (statisticsData: StatisticsData): LayerChartSummarySection | null => {
	const { chartData, chartStatistic } = statisticsData;
	if (chartData?.name !== 'consumption' || !chartStatistic?.available) return null;
	const name = readableName(chartData, chartData.name);
	const intro = periodIntro(chartData, chartData.series?.[0]?.data || []);
	const total = [chartStatistic.totalConsumed, chartStatistic.totalCost].filter(Boolean).join(' ');
	if (!total) return null;
	return {
		summaryTitle: summaryTitle(chartData),
		summary: $format('layer_chart_consumption_summary', {
			default: `${intro}, total ${name} was ${total}.`,
			values: { intro, name, total },
		}),
		aboutTitle: $format('layer_chart_about_title', {
			default: `About ${titleCase(name)}`,
			values: { name: titleCase(name) },
		}),
		about: $format('layer_chart_consumption_about', {
			default: `For this ${periodName(chartData)}, average ${name} was ${chartStatistic.average || '-'}, ranging from ${chartStatistic.min || '-'} to ${chartStatistic.max || '-'}.`,
			values: {
				period: periodName(chartData),
				name,
				average: chartStatistic.average || '-',
				min: chartStatistic.min || '-',
				max: chartStatistic.max || '-',
			},
		}),
	};
};

export const buildLayerChartSummarySections = (
	statisticsData: StatisticsData,
): LayerChartSummarySection[] => {
	const { chartData } = statisticsData;
	const visibleSeries = (chartData?.series || []).filter(
		(series) => series.visible !== false && !series.prevPeriod && !series.missingData,
	);
	const consumptionSection = buildConsumptionSection(statisticsData);
	if (consumptionSection) return [consumptionSection];

	if (chartData?.metric === 'consumption' || chartData?.name === 'consumption') {
		const primarySeries = visibleSeries.find((series) => !/(returned|reversed)/i.test(series.name));
		const section = primarySeries
			? buildMeasurementSection(chartData, primarySeries.name, primarySeries.data || [])
			: null;
		return section ? [section] : [];
	}

	const minMaxStats = getMinMaxStats(visibleSeries);
	if (minMaxStats) {
		const introData =
			visibleSeries.find((series) => isAverageSeries(series.name))?.data ||
			visibleSeries.find((series) => isMaxSeries(series.name))?.data ||
			[];
		return [buildMeasurementSection(chartData, chartData?.name, introData, minMaxStats)].filter(
			Boolean,
		) as LayerChartSummarySection[];
	}

	return visibleSeries
		.map((series) => buildMeasurementSection(chartData, series.name, series.data || []))
		.filter(Boolean) as LayerChartSummarySection[];
};
